import React, {useEffect, useState} from 'react';
import {
  Button,
  Form,
  Popconfirm,
  Select,
  Table,
  Col,
  Row,
  Divider,
  Input,
  Tooltip,
  Modal,
  Spin,
} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import {
  DescriptionItem,
  EventRegistrationStatus,
  OnlyAlphabetInputValidatorWithSpecialCharacters,
  PageSizeArray,
  PaginationPosition,
  ReturnNAOnEmpty,
  attendingDinnerLabel,
  calculateProcessingFeeForRegistrations,
  formatCurrencyByLocalChapter,
  formatDate,
  formatUtcToLocalDateTime,
  getStatusInfoOfEventRegistration,
  hasProcessingFeeExplicitlyDisabled,
  meetingPresentationLabel,
  registrationFilterOptions,
  sponsorFilters,
} from '../../../Util/commonUtility';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import CommonTableColumnTextView from '../../CommonComponents/CommonTableColumnTextView';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import AdminViewDetailsDrawer from '../../CommonComponents/AdminViewDetailsDrawer';
import CommonUserModal from '../../CommonComponents/CommonUserModal/index';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard/index';
import ReceiptComponent from '../../CommonComponents/receiptComponent';
import AdminSponsorEditModal from './AdminSponsorEditModal';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Option} from 'antd/es/mentions';
import {usePermissions} from '../../../hooks/usePermission';
import {getAllChapters} from '../../../Store/Actions';
import {fetchVendorRepsByRegistrationId} from '../../../Store/Actions/sponsors';
import {memorizedInitiatedRefundAttendees} from '../../../Store/Actions/eventRegister';
import {editRefundStatusById} from '../../../Store/Actions/events';
import {FETCH_ERROR} from '../../../Constant/ActionTypes';
import useLocalization from '../../../Localization';
import {showOverCapacityWarningModal} from '../EventRegistration/showOverCapacityWarningModal';
import {showSpotOpenedModal} from '../EventRegistration/showSpotOpenedModal';

const SponsorsComponent = (props) => {
  const {
    allSponsorData,
    fetchSponsor,
    paging,
    handlePaginationChange,
    sortParams,
    handleTableChange,
    searchValues,
    totalPaidAmount,
    totalDiscountedAmount,
    totalCreditAmount,
    totalRefundAmount,
    UpdateRegistrationOptions,
    cancelEventRegistrations,
    deleteToBePaidRegistration,
    updateSponsorAssets,
  } = props;

  const {ApplicationString} = useLocalization();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const ps = useSelector(({pageSizes}) => pageSizes.pageSizes.sponsors);
  const pn = useSelector(({pageSizes}) => pageSizes.pageNumbers.sponsors);

  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRegistrationWithRecord, setSelectedRegistrationWithRecord] =
    useState();
  const [showApproveCancelRegistration, setShowApproveCancelRegistration] =
    useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCancelAndRefundAccessed, setIsCancelAndRefundAccessed] =
    useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [assetModalRecord, setAssetModalRecord] = useState(null);
  const [assetModalSubmitting, setAssetModalSubmitting] = useState(false);
  const [receiptDownloadPayload, setReceiptDownloadPayload] = useState(null);
  const [vendorRepsModalRecord, setVendorRepsModalRecord] = useState(null);
  const [vendorRepsRows, setVendorRepsRows] = useState([]);
  const [vendorRepsLoading, setVendorRepsLoading] = useState(false);

  const isEditing = (record) =>
    editingKey != null && String(record.id) === String(editingKey);

  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const userRole = userInfo?.role;
  const {checkPermission} = usePermissions(userRole);
  const allChapters = useSelector((state) => state.chapters.allChapters);

  const getChapterDataForRecord = (record) =>
    allChapters?.data?.chapters?.find(
      (chapter) => chapter.id === record?.local_chapter_id,
    );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!allChapters) {
      dispatch(getAllChapters());
    }
  }, [allChapters]);

  useEffect(() => {
    if (
      !checkPermission('events_registrations', 'cancelAndRefundRegistration')
    ) {
      setIsCancelAndRefundAccessed(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  useEffect(() => {
    if (selectedRowKeys?.length > 0) {
      setShowApproveCancelRegistration(true);
    } else {
      setShowApproveCancelRegistration(false);
    }
  }, [selectedRowKeys]);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleSearch = (values) => {
    fetchSponsor(values);
  };

  // Drawer functions ----------------------------------------------------
  const showDrawer = (record) => {
    setOpenDrawerDetails(record);
    setOpen(true);
  };

  const onCloseDrawer = () => {
    setOpen(false);
  };

  const getProcessingFee = (record) => {
    const paidAmount = Number(record?.paid_amount || 0);
    if (paidAmount <= 0) return 0;
    if (hasProcessingFeeExplicitlyDisabled(record)) return 0;

    const transactionProcessingFee = Number(record?.processing_fee_amount || 0);
    const transactionBaseAmount = Number(record?.payment_base_amount || 0);
    if (transactionProcessingFee > 0 && transactionBaseAmount > 0) {
      return (
        Math.round(
          (paidAmount / transactionBaseAmount) * transactionProcessingFee * 100,
        ) / 100
      );
    }

    const recordWithoutTransactionFee = {
      ...record,
      processing_fee_amount: undefined,
      processing_fee: undefined,
      payment_processing_fee: undefined,
      transaction_fee: undefined,
      card_processing_fee: undefined,
    };

    return calculateProcessingFeeForRegistrations(
      [
        {
          ...recordWithoutTransactionFee,
          total_cost: paidAmount,
          discount_amount: 0,
        },
      ],
      recordWithoutTransactionFee,
    );
  };

  const getPaidAmountWithProcessingFee = (record) => {
    const paidAmount = Number(record?.paid_amount || 0);
    if (paidAmount <= 0) return 0;
    return Math.round((paidAmount + getProcessingFee(record)) * 100) / 100;
  };

  const SponsorsDrawerContent = () => {
    const record = openDrawerDetails || {};
    const localChapterData = getChapterDataForRecord(record);
    const {
      attendee_first_name,
      attendee_last_name,
      user_id,
      event_id,
      event_name,
      attendee_email,
      attendee_company,
      registration_type,
      event_registration_options,
      total_cost,
      discount_amount,
      discount_code,
      status,
      created_at,
      user_local_chapter,
      payment_id,
      payment_country,
      payment_zip,
      payment_date,
      card_last4,
      event_type,
      local_chapter_name,
      event_place,
    } = record;

    return (
      <>
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Attendee Status'
              content={
                getStatusInfoOfEventRegistration(status, ApplicationString)
                  ?.label
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='Event Name' content={event_name || 'N/A'} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Event Id' content={event_id} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Event Type'
              content={event_type || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Local Chapter'
              content={local_chapter_name || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='Location' content={event_place || 'N/A'} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Registration Type'
              content={registration_type || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='User Id' content={user_id} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Sponsorship Type'
              content={event_registration_options?.sponsorship?.name || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='First Name' content={attendee_first_name} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Last Name' content={attendee_last_name} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='Attendee Email' content={attendee_email} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Attendee Company'
              content={attendee_company}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Total Cost'
              content={formatCurrencyByLocalChapter(
                total_cost ? total_cost : 0,
                localChapterData,
              )}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Discounted Cost'
              content={formatCurrencyByLocalChapter(
                discount_amount ? discount_amount : 0,
                localChapterData,
              )}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Coupon Code Applied'
              content={discount_code || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Registered Date'
              content={formatDate(created_at)}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='User Local Chapter'
              content={user_local_chapter}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Stripe Payment ID'
              content={payment_id || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Stripe Payment Date'
              content={
                payment_date
                  ? formatUtcToLocalDateTime(payment_date) || 'N/A'
                  : 'N/A'
              }
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Credit Card Number'
              content={card_last4 ? `**** **** **** ${card_last4}` : 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Billing Country'
              content={payment_country || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Billing ZIP'
              content={payment_zip || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
      </>
    );
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowIds, selectedKeys) => {
      setSelectedRowKeys(selectedKeys?.map((row) => row.id));
      setSelectedRegistrationWithRecord(selectedKeys);
    },
    getCheckboxProps: (record) => {
      const disabledStatuses = [
        EventRegistrationStatus.PAYMENT_REFUNDED,
        EventRegistrationStatus.TO_BE_DELETED,
        EventRegistrationStatus.REFUND_PENDING,
        EventRegistrationStatus.TO_BE_PAID,
      ];
      // $0 rows stay selectable for Cancel; Initiate Refund filters paid_amount > 0.
      return {
        disabled: disabledStatuses.includes(Number(record.status)),
      };
    },
  };

  const handleCancelGroupOfRegistration = async () => {
    const result = await cancelEventRegistrations(
      {eventRegistrationIds: selectedRowKeys},
      setSelectedRowKeys,
    );
    if (result?.spotOpened) {
      showSpotOpenedModal();
    }
  };

  const handleInitiateRefundVisible = () => {
    const hasRefundable = (selectedRegistrationWithRecord || []).some(
      (row) => Number(row?.paid_amount || 0) > 0,
    );
    if (!hasRefundable) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          'Refund can only be initiated for registrations with a paid amount greater than $0. Use Cancel for free / $0 vendor reps.',
      });
      return;
    }
    setIsModalVisible(true);
  };
  const hideModal = () => {
    setIsModalVisible(false);
  };

  const handleInitiateRefund = () => {
    if (!selectedRowKeys || selectedRowKeys.length <= 0) {
      dispatch({
        type: FETCH_ERROR,
        payload: 'Please select sponsor registration(s)',
      });
      return;
    }

    const refundableRows = (selectedRegistrationWithRecord || []).filter(
      (row) => Number(row?.paid_amount || 0) > 0,
    );
    if (refundableRows.length <= 0) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          'Refund can only be initiated for registrations with a paid amount greater than $0. Use Cancel for free / $0 vendor reps.',
      });
      return;
    }

    const eventIds = new Set(refundableRows.map((record) => record.event_id));
    if (eventIds.size > 1) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          'Please select sponsor registrations belonging to the same event to initiate a refund.',
      });
      return;
    }

    const [eventId] = eventIds;
    const refundableIds = refundableRows.map((row) => row.id);
    dispatch(memorizedInitiatedRefundAttendees(refundableRows));
    dispatch(
      editRefundStatusById({
        eventRegistrationIds: refundableIds,
        status: 2,
      }),
    );
    hideModal();
    navigate(`/admin/eventRefund/${eventId}`);
  };

  const footer = () => {
    return (
      <div>
        {showApproveCancelRegistration && (
          <div className='admin-registration-container'>
            <Popconfirm
              title='Cancel Selected Registration'
              description='Are you sure you want to cancel the selected registrations?'
              onConfirm={handleCancelGroupOfRegistration}
              okType='primary'
              icon={<QuestionCircleOutlined style={{color: 'warning'}} />}>
              <div className='common-cancel-button'>
                <Button
                  id='SponsorsComponent-cancelRegistration'
                  className='common-submit-login common-form-submit'>
                  Proceed to Cancel Selected Registration
                </Button>
              </div>
            </Popconfirm>
            <CommonUserModal
              visible={isModalVisible}
              onCancel={hideModal}
              onConfirm={handleInitiateRefund}
              confirmBtnText={'Proceed'}
              content={
                <React.Fragment>
                  Are you sure you want to initiate a refund for the selected
                  registration(s)?
                </React.Fragment>
              }
              usedForAdminSide={true}
            />
            <div className='common-cancel-button'>
              <Button
                onClick={handleInitiateRefundVisible}
                id='SponsorsComponent-initiateRefund'
                className='common-submit-login common-form-submit'>
                Initiate Refund
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const edit = (record) => {
    form.setFieldsValue({
      attendee_first_name: record.attendee_first_name,
      attendee_last_name: record.attendee_last_name,
      attendee_email: record.attendee_email,
      attendee_company: record.attendee_company,
      status: record.status,
    });
    setEditingKey(record.id);
  };

  const exitEditMode = () => {
    setEditingKey(null);
    form.resetFields();
  };

  const cancel = () => {
    exitEditMode();
  };

  const save = async (record) => {
    try {
      const values = await form.validateFields();
      const payload = {
        attendee_first_name: values.attendee_first_name,
        attendee_last_name: values.attendee_last_name,
        attendee_email: values.attendee_email,
        attendee_company: values.attendee_company,
        status: values.status,
      };

      const performSave = async ({confirmOverCapacity = false} = {}) => {
        const result = await UpdateRegistrationOptions(
          {
            ...payload,
            ...(confirmOverCapacity ? {confirm_over_capacity: true} : {}),
          },
          record?.id,
          exitEditMode,
        );

        if (result?.success) {
          exitEditMode();
          return true;
        }

        if (result?.requiresOverCapacityConfirmation && !confirmOverCapacity) {
          await showOverCapacityWarningModal({
            fallbackMessage: result.errorMessage,
            onConfirm: () => performSave({confirmOverCapacity: true}),
          });
          return false;
        }

        return false;
      };

      await performSave();
    } catch (err) {
      console.error('Validate Failed:', err);
    }
  };

  const shouldEditCell = (column) => (column.editable ? true : false);

  const openAssetModal = (record) => {
    setAssetModalRecord(record);
  };

  const closeAssetModal = () => {
    setAssetModalRecord(null);
    setAssetModalSubmitting(false);
  };

  const handleAssetModalSubmit = (formData, eventRegistrationId) => {
    setAssetModalSubmitting(true);
    updateSponsorAssets(
      formData,
      eventRegistrationId,
      () => {
        closeAssetModal();
      },
      () => {
        setAssetModalSubmitting(false);
      },
    );
  };

  const canShowReceipt = (record) => {
    const receiptRef = record?.transaction_id || record?.payment_id;
    const status = Number(record?.status);
    const isPaid = [
      EventRegistrationStatus.PAID,
      EventRegistrationStatus.PAYMENT_CONFIRMED,
      EventRegistrationStatus.FEE_WAIVER,
      EventRegistrationStatus.NO_CHARGE_EVENT,
    ].includes(status);
    return Boolean(receiptRef && isPaid);
  };

  const buildReceiptEventData = (record, vendorReps = []) => {
    if (!record) return null;
    const relatedRows = [record, ...(vendorReps || [])];
    const transactionKey = record?.transaction_id || record?.payment_id || null;

    return {
      eventName: record?.event_name,
      location: record?.event_place || null,
      chapter: record?.local_chapter_name || null,
      event_start_date_time: record?.event_start_date_time || null,
      event_end_date_time: record?.event_end_date_time || null,
      is_available_for_webcast: Boolean(record?.is_available_for_webcast),
      sponsors: relatedRows,
      payments: transactionKey
        ? [
            {
              transaction_key: transactionKey,
              billing_country: record.payment_country,
              billing_zip: record.payment_zip,
              card_last4: record.card_last4,
              payment_datetime: record.payment_date,
              payment_date: record.payment_date,
              amount: record.payment_amount,
              base_amount: record.payment_base_amount,
              is_processing_fee_added: record.is_processing_fee_added,
              processing_fee_percentage: record.processing_fee_percentage,
              processing_fee_amount: record.processing_fee_amount,
            },
          ]
        : [],
    };
  };

  const downloadReceipt = async (record) => {
    try {
      const vendorReps = await fetchVendorRepsByRegistrationId(record.id);
      setReceiptDownloadPayload({
        record,
        vendorReps,
      });
    } catch (error) {
      dispatch({
        type: FETCH_ERROR,
        payload: error?.response?.data?.errorMessage || 'Failed to prepare receipt',
      });
    }
  };

  const clearReceiptDownload = () => {
    setReceiptDownloadPayload(null);
  };

  const openVendorRepsModal = async (record) => {
    setVendorRepsModalRecord(record);
    setVendorRepsLoading(true);
    setVendorRepsRows([]);
    try {
      const vendorReps = await fetchVendorRepsByRegistrationId(record.id);
      setVendorRepsRows(vendorReps);
    } catch (error) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          error?.response?.data?.errorMessage ||
          'Failed to load vendor reps details',
      });
      setVendorRepsModalRecord(null);
    } finally {
      setVendorRepsLoading(false);
    }
  };

  const closeVendorRepsModal = () => {
    setVendorRepsModalRecord(null);
    setVendorRepsRows([]);
    setVendorRepsLoading(false);
  };

  const vendorRepsPerk =
    vendorRepsModalRecord?.event_registration_options?.sponsorship
      ?.event_sponsorship_perk ||
    vendorRepsModalRecord?.event_registration_options?.sponsorship
      ?.eventSponsorshipPerk ||
    vendorRepsRows?.[0]?.event_registration_options?.sponsorship
      ?.event_sponsorship_perk ||
    null;

  const vendorRepsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) =>
        `${record?.attendee_first_name || ''} ${
          record?.attendee_last_name || ''
        }`.trim() || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'attendee_email',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Company',
      dataIndex: 'attendee_company',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Registration Type',
      dataIndex: 'registration_type',
      render: (text) => text || 'N/A',
    },
    {
      title: meetingPresentationLabel(
        vendorRepsPerk?.attendees_price,
        ApplicationString,
      ),
      dataIndex: 'attending_presentation',
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: attendingDinnerLabel(
        vendorRepsPerk?.dinner_with_speaker_price,
        ApplicationString,
      ),
      dataIndex: 'attending_dinner',
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const {color, label} = getStatusInfoOfEventRegistration(
          status,
          ApplicationString,
        );
        return <span style={{color}}>{label}</span>;
      },
    },
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: true,
      fixed: isMobile ? '' : 'left',
      width: '5%',
    },
    {
      title: 'Event Name',
      dataIndex: 'event_name',
      sorter: true,
      editable: false,
      width: '10%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Member Name',
      dataIndex: 'user_name',
      sorter: true,
      editable: false,
      width: '10%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Member Email',
      dataIndex: 'user_email',
      sorter: true,
      editable: false,
      width: '10%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Attendee First Name',
      dataIndex: 'attendee_first_name',
      sorter: true,
      editable: true,
      width: '10%',
      render: (text) => (
        <CommonTableColumnTextView columnText={text} isToolTip={true} />
      ),
    },
    {
      title: 'Attendee Last Name',
      dataIndex: 'attendee_last_name',
      width: '10%',
      editable: true,
      sorter: true,
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'attendee_email',
      editable: true,
      sorter: true,
      width: '12%',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Sponsorship Type',
      dataIndex: 'type',
      editable: false,
      sorter: false,
      width: '10%',
      render: (text, record) => (
        <div>
          {record?.event_registration_options?.sponsorship?.name || 'Sponsor'}
        </div>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'attendee_company',
      editable: true,
      width: '8%',
      sorter: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'User Local Chapter',
      dataIndex: 'user_local_chapter',
      width: '6%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '10%',
      editable: true,
      render: (status) => {
        const {color, label} = getStatusInfoOfEventRegistration(
          status,
          ApplicationString,
        );
        return <span style={{color}}>{label}</span>;
      },
    },
    {
      title: 'Coupon Code',
      dataIndex: 'discount_code',
      width: '6%',
      render: (text) => ReturnNAOnEmpty(text),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      width: '6%',
      render: (text, record) => (
        <div className='total-cost'>
          {formatCurrencyByLocalChapter(
            text || 0,
            getChapterDataForRecord(record),
          )}
        </div>
      ),
    },
    {
      title: 'Discounted Amount',
      dataIndex: 'discount_amount',
      width: '6%',
      render: (text, record) => (
        <div className='total-cost'>
          {formatCurrencyByLocalChapter(
            text || 0,
            getChapterDataForRecord(record),
          )}
        </div>
      ),
    },
    {
      title: 'Paid Amount (Includes Processing Fee)',
      dataIndex: 'paid_amount',
      width: '8%',
      render: (text, record) => {
        const processingFee = getProcessingFee(record);
        const paidAmountWithProcessingFee =
          getPaidAmountWithProcessingFee(record);
        const localChapterData = getChapterDataForRecord(record);
        return (
          <Tooltip
            color='#fff'
            overlayInnerStyle={{color: '#111'}}
            title={
              processingFee > 0
                ? `Processing fee added: ${formatCurrencyByLocalChapter(
                    processingFee,
                    localChapterData,
                    2,
                    2,
                  )}`
                : 'No processing fee added.'
            }>
            <div className='total-cost'>
              {formatCurrencyByLocalChapter(
                paidAmountWithProcessingFee,
                localChapterData,
                2,
                2,
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Credit Amount',
      dataIndex: 'credit_amount',
      width: '6%',
      render: (text, record) => (
        <div className='total-cost'>
          {formatCurrencyByLocalChapter(
            text || 0,
            getChapterDataForRecord(record),
          )}
        </div>
      ),
    },
    {
      title: 'Refunded Amount',
      dataIndex: 'refunded_amount',
      width: '6%',
      render: (text, record) => (
        <div className='total-cost'>
          {formatCurrencyByLocalChapter(
            text || 0,
            getChapterDataForRecord(record),
          )}
        </div>
      ),
    },
    {
      title: 'Refund Method',
      dataIndex: 'refund_method',
      width: '8%',
      render: (text, record) => (
        <div className='total-cost'>
          {record?.refunded_amount > 0
            ? record?.refund_to_account
              ? 'Refund in PBSS Credit'
              : 'Refund in Bank Account'
            : 'N/A'}
        </div>
      ),
    },
    {
      title: 'Registration Date',
      dataIndex: 'registration_date',
      sorter: true,
      width: '10%',
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Stripe Payment ID',
      dataIndex: 'payment_id',
      width: '12%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Credit Card Number',
      dataIndex: 'card_last4',
      width: '8%',
      render: (text) => <span>{text ? `**** **** **** ${text}` : 'N/A'}</span>,
    },
    {
      title: 'Stripe Payment Date',
      dataIndex: 'payment_date',
      width: '10%',
    },
    {
      title: 'Billing Country',
      dataIndex: 'payment_country',
      width: '6%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Billing ZIP',
      dataIndex: 'payment_zip',
      width: '6%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      width: '10%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: '',
      key: 'action',
      fixed: isMobile ? '' : 'right',
      width: '10%',
      render: (_, record) => (
        <AdminActionsDropdown
          editBool={true}
          record={record}
          usedForTable
          viewBool={true}
          handleView={showDrawer}
          usedForAttendeeTable={true}
          handleCancelClick={cancel}
          attendeeEditingKey={editingKey}
          handleEditClick={edit}
          handleSaveClick={save}
          text='Sponsor Registration'
          usedForAdminSide={true}
          resource='events_registrations'
          customActionBool={true}
          customActionLabel='Upload Logo'
          handleCustomActionClick={openAssetModal}
          vendorRepsViewBool={true}
          handleVendorRepsViewClick={openVendorRepsModal}
          receiptBool={canShowReceipt(record)}
          handleReceiptClick={downloadReceipt}
          deleteBool={record.status === EventRegistrationStatus.TO_BE_PAID}
          handleDeleteClick={(registrationId) =>
            deleteToBePaidRegistration(registrationId, () =>
              fetchSponsor(searchValues),
            )
          }
        />
      ),
    },
  ];

  columns.forEach((column) => {
    if (column.key !== 'action') {
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  const mergedColumns = columns.map((col) => ({
    ...col,
    onCell: (record) => ({
      record,
      dataIndex: col.dataIndex,
      title: col.title,
      editing: isEditing(record) && shouldEditCell(col),
      options: col.dataIndex,
    }),
  }));

  const totalAmount =
    (totalPaidAmount || 0) +
    (totalCreditAmount || 0) +
    (totalDiscountedAmount || 0) -
    (totalRefundAmount || 0);

  return (
    <div className='main-container'>
      <AdminSponsorEditModal
        open={!!assetModalRecord}
        record={assetModalRecord}
        onClose={closeAssetModal}
        onSubmit={handleAssetModalSubmit}
        submitting={assetModalSubmitting}
      />
      {receiptDownloadPayload?.record ? (
        <div style={{display: 'none'}} aria-hidden='true'>
          <ReceiptComponent
            eventData={buildReceiptEventData(
              receiptDownloadPayload.record,
              receiptDownloadPayload.vendorReps,
            )}
            eventLocalChapterData={getChapterDataForRecord(
              receiptDownloadPayload.record,
            )}
            eventRegistrationType='sponsorship'
            hideEventType
            autoDownload
            showDownloadButton={false}
            onDownloadComplete={clearReceiptDownload}
            showTransactionKey
          />
        </div>
      ) : null}
      <Modal
        title={`Vendor Reps Details${
          vendorRepsModalRecord?.event_name
            ? ` - ${vendorRepsModalRecord.event_name}`
            : ''
        }`}
        open={!!vendorRepsModalRecord}
        onCancel={closeVendorRepsModal}
        footer={null}
        width={960}
        destroyOnClose
        centered>
        <Spin spinning={vendorRepsLoading}>
          <Table
            rowKey='id'
            size='small'
            pagination={false}
            columns={vendorRepsColumns}
            dataSource={vendorRepsRows}
            locale={{emptyText: 'No vendor reps found for this sponsor.'}}
            scroll={{x: 900}}
          />
        </Spin>
      </Modal>
      <AdminViewDetailsDrawer
        content={SponsorsDrawerContent}
        visible={open}
        text={'Sponsor Registration'}
        onCloseDrawer={onCloseDrawer}
        isEditable={false}
      />
      <HeaderSearchCard
        resource='sponsors'
        toggleClick={toggleExpand}
        expanded={expanded}
        showAddButton={false}
      />
      {expanded && (
        <DynamicSearchForm
          columns={sponsorFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'sponsors'}
        />
      )}
      <div className='listing-container'>
        <Form form={form} component={false}>
          <Table
            bordered
            className='admin-table'
            components={{
              body: {
                cell: (cellProps) => <EditableCell {...cellProps} />,
              },
            }}
            rowSelection={
              isCancelAndRefundAccessed
                ? {
                    type: 'checkbox',
                    ...rowSelection,
                  }
                : undefined
            }
            footer={footer}
            columns={mergedColumns}
            dataSource={allSponsorData}
            rowKey={'id'}
            onChange={handleTableChange}
            sticky={true}
            scroll={{x: 2400}}
            pagination={{
              onChange: handlePaginationChange,
              current: pn || paging.page,
              total: paging.totalCount,
              pageSize: ps || paging.pageCount,
              pageSizeOptions: PageSizeArray,
              showQuickJumper: true,
              showSizeChanger: true,
              onShowSizeChange: handlePaginationChange,
              showTotal: (total) =>
                `Credit Amount: ${formatCurrencyByLocalChapter(
                  totalCreditAmount || 0,
                  'USD',
                )} | Paid Amount: ${formatCurrencyByLocalChapter(
                  totalPaidAmount || 0,
                  'USD',
                )} | Discounted Amount: ${formatCurrencyByLocalChapter(
                  totalDiscountedAmount || 0,
                  'USD',
                )} | Total Amount: ${formatCurrencyByLocalChapter(
                  totalAmount,
                  'USD',
                )} | Total ${total} items`,
              position: [PaginationPosition],
            }}
          />
        </Form>
      </div>
    </div>
  );
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  const inputNode = (() => {
    switch (dataIndex) {
      case 'status':
        return (
          <Select
            dropdownStyle={{minWidth: 130}}
            placeholder={`Select your status for ${title}`}>
            {registrationFilterOptions?.map((regOption) => (
              <Option
                key={regOption.label}
                value={regOption.value}
                disabled={regOption.value === 0 || regOption.value === -1}>
                {regOption.label}
              </Option>
            ))}
          </Select>
        );
      case 'attendee_email':
        return (
          <Form.Item
            name={dataIndex}
            className='no-margin'
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
              {
                type: 'email',
                message: 'Please enter a valid email address',
              },
            ]}>
            <Input
              onCopy={(e) => e.preventDefault()}
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
      case 'attendee_company':
        return (
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
            ]}
            style={{margin: 0}}>
            <Input
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
      default:
        return (
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(value, title),
              },
            ]}
            style={{margin: 0}}>
            <Input
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
    }
  })();

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{margin: 0}}
          rules={[{required: true, message: ''}]}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default SponsorsComponent;
